import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Cloud, Download, ExternalLink, Info, LogIn, LogOut, Menu, Moon, QrCode, Save, Share2, Sun, Trash2, Upload, UserPlus, Volume2, VolumeX, X } from 'lucide-react'
import type { SavedQrCode } from '../domain/qr'
import { activeSavedCodes, CODE_TYPES_HELP_URL, parseSavedCodes } from '../domain/qr'
import { LOCALES, useI18n } from '../i18n/context'
import type { AccountSyncControls } from '../hooks/useAccountSync'
import type { Screen } from '../App'
import { CODE_HOOVER_APP_URL } from '../constants/links'
import { isIosInstallDevice, isStandaloneApp } from '../lib/pwa'
import { FixedCodeModal } from './FixedCodeModal'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

interface HeaderProps {
  codes: SavedQrCode[]
  setCodes: (codes: SavedQrCode[]) => void
  setScreen: (screen: Screen) => void
  dark: boolean
  setDark: (dark: boolean) => void
  soundEnabled: boolean
  setSoundEnabled: (enabled: boolean) => void
  autoSaveScans: boolean
  setAutoSaveScans: (enabled: boolean) => void
  playSoundPreview: () => void
  playComplete: () => void
  playSuccess: () => void
  accountSync: AccountSyncControls
}

export function Header({ codes, setCodes, setScreen, dark, setDark, soundEnabled, setSoundEnabled, autoSaveScans, setAutoSaveScans, playSoundPreview, playComplete, playSuccess, accountSync }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openDeviceModal, setOpenDeviceModal] = useState(false)
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIosInstallHelp, setShowIosInstallHelp] = useState(false)
  const [installed, setInstalled] = useState(isStandaloneApp)
  const [accountEmail, setAccountEmail] = useState('')
  const [accountPassword, setAccountPassword] = useState('')
  const [clearAllCodesOpen, setClearAllCodesOpen] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const desktopMenu = useRef<HTMLDetailsElement>(null)
  const clearAllCancelButton = useRef<HTMLButtonElement>(null)
  const { locale, setLocale, t } = useI18n()
  const closeMenus = () => {
    setMobileMenuOpen(false)
    desktopMenu.current?.removeAttribute('open')
  }

  useEffect(() => {
    const closeDesktopMenuOnOutsideClick = (event: PointerEvent) => {
      if (!desktopMenu.current?.open) return
      if (desktopMenu.current.contains(event.target as Node)) return
      desktopMenu.current.removeAttribute('open')
    }

    document.addEventListener('pointerdown', closeDesktopMenuOnOutsideClick)
    return () => document.removeEventListener('pointerdown', closeDesktopMenuOnOutsideClick)
  }, [])

  useEffect(() => {
    const captureInstallPrompt = (event: Event) => {
      event.preventDefault()
      setInstallPrompt(event as BeforeInstallPromptEvent)
      setShowIosInstallHelp(false)
    }

    const markInstalled = () => {
      setInstallPrompt(null)
      setShowIosInstallHelp(false)
      setInstalled(true)
    }

    window.addEventListener('beforeinstallprompt', captureInstallPrompt)
    window.addEventListener('appinstalled', markInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', captureInstallPrompt)
      window.removeEventListener('appinstalled', markInstalled)
    }
  }, [])

  useEffect(() => {
    if (!clearAllCodesOpen) return
    clearAllCancelButton.current?.focus()

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setClearAllCodesOpen(false)
    }

    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [clearAllCodesOpen])

  const importCodes = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    void file.text().then((text) => {
      try {
        setCodes(activeSavedCodes(parseSavedCodes(text)))
      } catch {
        window.alert(t('default-invalid-json'))
      }
      event.target.value = ''
    })
  }

  const exportCodes = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(codes, null, 2)], { type: 'application/json' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'codes.json'
    link.click()
    URL.revokeObjectURL(url)
    closeMenus()
  }

  const requestClearAllCodes = () => {
    if (!codes.length) return
    setClearAllCodesOpen(true)
    closeMenus()
  }

  const clearAllCodes = () => {
    setCodes([])
    setClearAllCodesOpen(false)
  }

  const runCloudAction = (action: () => Promise<void>) => {
    void action().catch(() => undefined)
  }

  const createAccount = () => {
    const email = accountEmail.trim()
    if (!email || !accountPassword) return
    runCloudAction(async () => {
      await accountSync.register(email, accountPassword)
      setAccountPassword('')
      playSuccess()
    })
  }

  const signIn = () => {
    const email = accountEmail.trim()
    if (!email || !accountPassword) return
    runCloudAction(async () => {
      await accountSync.signIn(email, accountPassword)
      setAccountPassword('')
      playSuccess()
    })
  }

  const deleteAccount = () => {
    if (!window.confirm(t('default-account-sync-delete-confirm'))) return
    if (window.prompt(t('default-account-sync-delete-type-confirm')) !== 'DELETE') return
    runCloudAction(accountSync.deleteAccount)
  }

  const toggleSound = (enabled: boolean) => {
    setSoundEnabled(enabled)
    if (enabled) window.setTimeout(playSoundPreview, 0)
  }

  const toggleTheme = (enabled: boolean) => {
    setDark(enabled)
    closeMenus()
  }

  const navigateHome = (play = false) => {
    if (play) playComplete()
    setScreen('codes')
    closeMenus()
  }

  const installCodeHoover = () => {
    if (!installPrompt) {
      if (isIosInstallDevice()) {
        setShowIosInstallHelp(true)
        return
      }
      window.alert(t('default-install-app-fallback'))
      closeMenus()
      return
    }

    void installPrompt.prompt().then(() => installPrompt.userChoice).finally(() => {
      setInstallPrompt(null)
      closeMenus()
    })
  }

  const accountBusy = accountSync.status.state === 'syncing'
  const selectedLocale = LOCALES.find(({ id }) => id === locale) ?? LOCALES[0]

  const menuItems = (
    <>
      <li>
        <div className="!flex min-w-0 flex-col items-stretch gap-2 rounded-md border border-base-300 bg-base-100 p-3 text-base-content">
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2 text-sm font-medium"><Cloud size={18} />{t('default-account-sync')}</span>
            <span className={`badge badge-sm ${accountSync.signedIn ? 'badge-neutral' : 'badge-ghost'}`}>{accountSync.signedIn ? t('default-on') : t('default-off')}</span>
          </div>
          <p className="m-0 text-xs opacity-80">{t(accountSync.status.messageId)}</p>
          {accountSync.signedIn && <p className="m-0 truncate text-xs font-medium opacity-80">{accountSync.email}</p>}
          {accountSync.signedIn ? (
            <div className="grid grid-cols-2 gap-2">
              <button type="button" className="btn btn-xs" disabled={accountBusy} onClick={() => runCloudAction(accountSync.signOut)}><LogOut size={14} />{t('default-account-sync-sign-out')}</button>
              <button type="button" className="btn btn-xs btn-error" disabled={accountBusy} onClick={deleteAccount}><Trash2 size={14} />{t('default-account-sync-delete')}</button>
            </div>
          ) : (
            <form className="flex min-w-0 flex-col gap-2" onSubmit={(event) => { event.preventDefault(); signIn() }}>
              <input
                className="input input-sm input-bordered min-w-0 w-full"
                value={accountEmail}
                type="email"
                autoComplete="email"
                placeholder={t('default-account-email')}
                aria-label={t('default-account-email')}
                onChange={(event) => setAccountEmail(event.target.value)}
              />
              <input
                className="input input-sm input-bordered min-w-0 w-full"
                value={accountPassword}
                type="password"
                autoComplete="current-password"
                placeholder={t('default-account-password')}
                aria-label={t('default-account-password')}
                onChange={(event) => setAccountPassword(event.target.value)}
              />
              <div className="grid min-w-0 grid-cols-2 gap-2">
                <button type="submit" className="btn btn-sm btn-neutral min-w-0" disabled={accountBusy || !accountEmail.trim() || accountPassword.length < 8}><LogIn size={14} />{t('default-account-sign-in')}</button>
                <button type="button" className="btn btn-sm min-w-0" disabled={accountBusy || !accountEmail.trim() || accountPassword.length < 8} onClick={createAccount}><UserPlus size={14} />{t('default-account-create')}</button>
              </div>
            </form>
          )}
        </div>
      </li>
      <li>
        <div className="!flex flex-wrap items-center justify-between gap-2 rounded-md border border-base-300 bg-base-100 px-3 py-2 text-base-content">
          <span className="text-sm font-medium">{t('default-language')}: {selectedLocale.name}</span>
          <span className="flex flex-wrap justify-end gap-1">
            {LOCALES.map(({ id, flag, name }) => (
              <button
                key={id}
                type="button"
                className={`btn btn-ghost btn-sm w-8 text-2xl filter hover:grayscale-0 ${locale === id ? 'grayscale-0' : 'grayscale'}`}
                title={name}
                aria-label={name}
                onClick={() => { setLocale(id); closeMenus() }}
              >{flag}</button>
            ))}
          </span>
        </div>
      </li>
      <li>
        <label className={`!flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 ${dark ? 'border-neutral bg-neutral text-neutral-content' : 'border-base-300 bg-base-100 text-base-content'}`}>
          <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
            {dark ? <Moon size={18} aria-hidden="true" /> : <Sun size={18} aria-hidden="true" />}
            <span>{t('default-dark-mode')}</span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <span className={`badge badge-sm min-w-10 border-0 px-2 text-xs font-medium uppercase ${dark ? 'bg-neutral-content text-neutral' : 'bg-base-300 text-base-content'}`}>
              {dark ? t('default-on') : t('default-off')}
            </span>
            <input type="checkbox" className="toggle toggle-sm" checked={dark} aria-label={t('default-dark-mode')} onChange={(event) => toggleTheme(event.target.checked)} />
          </span>
        </label>
      </li>
      <li>
        <label className={`!flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 ${soundEnabled ? 'border-neutral bg-neutral text-neutral-content' : 'border-base-300 bg-base-100 text-base-content'}`}>
          <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
            {soundEnabled ? <Volume2 size={18} aria-hidden="true" /> : <VolumeX size={18} aria-hidden="true" />}
            <span>{t('default-sound-effects')}</span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <span className={`badge badge-sm min-w-10 border-0 px-2 text-xs font-medium uppercase ${soundEnabled ? 'bg-neutral-content text-neutral' : 'bg-base-300 text-base-content'}`}>
              {soundEnabled ? t('default-on') : t('default-off')}
            </span>
            <input type="checkbox" className="toggle toggle-sm" checked={soundEnabled} aria-label={t('default-sound-effects')} onChange={(event) => toggleSound(event.target.checked)} />
          </span>
        </label>
      </li>
      <li>
        <label className={`!flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 ${autoSaveScans ? 'border-neutral bg-neutral text-neutral-content' : 'border-base-300 bg-base-100 text-base-content'}`}>
          <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
            <Save size={18} aria-hidden="true" />
            <span>{t('default-auto-save-scans')}</span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <span className={`badge badge-sm min-w-10 border-0 px-2 text-xs font-medium uppercase ${autoSaveScans ? 'bg-neutral-content text-neutral' : 'bg-base-300 text-base-content'}`}>
              {autoSaveScans ? t('default-on') : t('default-off')}
            </span>
            <input type="checkbox" className="toggle toggle-sm" checked={autoSaveScans} aria-label={t('default-auto-save-scans')} onChange={(event) => setAutoSaveScans(event.target.checked)} />
          </span>
        </label>
      </li>
      <li className="mt-2 border-t border-base-300 pt-2">
        <a className="w-full text-sm font-normal text-base-content no-underline" href={CODE_TYPES_HELP_URL} target="_blank" rel="noopener noreferrer" onClick={closeMenus}>
          <ExternalLink size={18} />{t('default-code-types')}
        </a>
      </li>
      {!installed && (
        <>
          <li>
            <button type="button" className="w-full text-left text-sm font-normal" onClick={installCodeHoover}>
              <Download size={18} />{t('default-install-app')}
            </button>
          </li>
          {showIosInstallHelp && (
            <li>
              <div className="!flex items-start gap-3 rounded-md border border-base-300 bg-base-100 px-3 py-2 text-sm text-base-content" role="status">
                <Share2 className="mt-0.5 shrink-0" size={18} aria-hidden="true" />
                <p className="m-0">{t('default-ios-install-instructions')}</p>
              </div>
            </li>
          )}
        </>
      )}
      <li><button type="button" className="w-full text-sm font-normal" onClick={() => { setOpenDeviceModal(true); closeMenus() }}><QrCode size={18} />{t('default-open-on-different-device')}</button></li>
      <li><button type="button" className="w-full text-sm font-normal" onClick={() => { fileInput.current?.click(); closeMenus() }}><Upload size={18} />{t('default-import-json')}</button></li>
      <li><button type="button" className="w-full text-sm font-normal" onClick={exportCodes}><Download size={18} />{t('default-export')}</button></li>
      <li className="mt-2 border-t border-base-300 pt-2"><button type="button" className="w-full text-left text-sm font-normal text-error" disabled={!codes.length} onClick={requestClearAllCodes}><Trash2 size={18} />{t('default-clear-all-codes')}</button></li>
      <li><button type="button" className="w-full text-left text-sm font-normal" onClick={() => { playComplete(); setScreen('about'); closeMenus() }}><Info size={18} />{t('default-about')}</button></li>
    </>
  )

  return (
    <header className="flex w-full flex-nowrap items-center gap-2 sm:gap-4">
      <input ref={fileInput} className="hidden" type="file" accept=".json,application/json" onChange={importCodes} />
      <button type="button" className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md text-left transition-opacity hover:opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current sm:gap-3" onClick={() => navigateHome(true)} aria-label={t('default-page-title')}>
        <img className="code-hoover-logo h-8 w-8 shrink-0 sm:h-10 sm:w-10" src="/favicon.svg" alt="Code Hoover 2.0 logo" />
        <h1 className="m-0 whitespace-nowrap p-0 text-lg font-bold leading-none text-primary min-[360px]:text-xl sm:text-3xl">{t('default-page-title')}</h1>
      </button>
      <div className="ms-auto flex shrink-0 items-center gap-2">
        <button type="button" className="btn btn-ghost btn-circle btn-sm lg:hidden" aria-label={t('default-open')} onClick={() => setMobileMenuOpen(true)}><Menu size={22} /></button>
        <details ref={desktopMenu} className="dropdown dropdown-end hidden lg:block">
          <summary className="btn btn-ghost btn-circle" aria-label={t('default-open')}><Menu /></summary>
          <ul className="menu menu-md dropdown-content z-50 mt-3 w-96 rounded-2xl border border-base-300 bg-base-200 p-3 text-base-content shadow-xl">{menuItems}</ul>
        </details>
      </div>
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-base-100 text-base-content lg:hidden" role="dialog" aria-modal="true" onClick={closeMenus}>
          <div className="flex h-full w-full flex-col bg-base-100" onClick={(event) => event.stopPropagation()}>
            <div className="flex shrink-0 items-center justify-between border-b border-base-300 px-4 py-3">
              <button type="button" className="flex cursor-pointer items-center gap-3 rounded-md text-left transition-opacity hover:opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current" onClick={() => navigateHome(true)} aria-label={t('default-page-title')}><img className="code-hoover-logo h-8 w-8" src="/favicon.svg" alt="" /><h2 className="m-0 text-lg font-semibold">{t('default-page-title')}</h2></button>
              <button type="button" className="btn btn-ghost btn-circle" aria-label={t('default-close')} onClick={closeMenus}><X /></button>
            </div>
            <ul className="menu menu-md min-h-0 w-full min-w-0 flex-1 flex-nowrap gap-2 overflow-x-hidden overflow-y-auto bg-base-100 p-4">{menuItems}</ul>
          </div>
        </div>
      )}
      {clearAllCodesOpen && (
        <div
          className="modal modal-open z-[200] bg-base-content/50 px-4 text-base-content"
          role="dialog"
          aria-modal="true"
          aria-labelledby="clear-all-codes-title"
          onClick={() => setClearAllCodesOpen(false)}
        >
          <section className="modal-box max-w-md rounded-2xl border border-base-300 bg-base-100 p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-error text-error-content">
                <Trash2 size={22} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h2 id="clear-all-codes-title" className="m-0 text-xl font-bold">{t('default-clear-all-codes')}</h2>
                <p className="m-0 mt-2 text-sm leading-relaxed opacity-75">{t('default-clear-all-codes-confirm', { count: codes.length })}</p>
              </div>
            </div>
            <div className="modal-action mt-6">
              <button ref={clearAllCancelButton} type="button" className="btn" onClick={() => setClearAllCodesOpen(false)}>{t('default-cancel')}</button>
              <button type="button" className="btn btn-error" onClick={clearAllCodes}><Trash2 size={17} aria-hidden="true" />{t('default-clear-all-codes')}</button>
            </div>
          </section>
        </div>
      )}
      {openDeviceModal && <FixedCodeModal url={CODE_HOOVER_APP_URL} label={t('default-open-on-different-device')} onClose={() => setOpenDeviceModal(false)} />}
    </header>
  )
}
